import { UserRole } from "constants/userRole";
import { Button } from "primereact/button";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetMyTrainingQuery } from "store/queries/training";
import { userSelector } from "store/slices/user";
import styled from "styled-components";

/** sessionStorage key: the banner stays hidden for the rest of this browser session once dismissed. */
export const TRAINING_BANNER_DISMISSED_KEY = "clearCertified.bannerDismissed";

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0.825rem 12px;
  padding: 8px 12px 8px 16px;
  border: 1px solid var(--brand-gray-200);
  border-radius: 8px;
  background: var(--brand-blue-25);
  font-size: 0.875rem;
`;

const Text = styled.span`
  flex: 1;
`;

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(TRAINING_BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    window.sessionStorage.setItem(TRAINING_BANNER_DISMISSED_KEY, "1");
  } catch {
    // Storage blocked (private window etc.): the banner still hides until the page reloads.
  }
}

interface ITrainingBannerProps {
  /** Overrides the Start / Resume action. PR 2 wires this to the tour. */
  onStart?: () => void;
}

/**
 * CLE-3015 Clear Certified — "Get Clear Certified" nag on the hospital view.
 *
 * Shown only when all of: the current account has `features.clearCertifiedTraining` on (the API's
 * `enabled`), the user is HospitalStaff (never a SuperAdmin), and the user is not certified yet.
 * Nothing is gated behind it. Dismissing hides it for this browser session only.
 *
 * Until the tour exists (PR 2), Start / Resume opens the hospital's Estimates tab with
 * `?training=1`, the parameter PR 2 reads to start the tour.
 */
export function TrainingBanner({ onStart }: ITrainingBannerProps) {
  const navigate = useNavigate();
  const { scope, currentAccount } = useSelector(userSelector);
  const isHospitalStaff = scope === UserRole.HOSPITAL_STAFF;

  const { data } = useGetMyTrainingQuery(currentAccount, {
    skip: !isHospitalStaff || !currentAccount,
  });
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const record = data?.data;
  if (!isHospitalStaff || !record?.enabled || record.clearCertifiedAt) {
    return null;
  }
  if (dismissed) {
    return null;
  }

  const resuming = Boolean(record.trainingProgress?.currentStepId);

  const handleStart = () => {
    if (onStart) {
      onStart();
      return;
    }
    navigate(`/hospitals/view/${currentAccount}/orders?training=1`);
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  return (
    <Banner role="region" aria-label="Clear Certified training">
      <i className="pi pi-verified text-primary" aria-hidden />
      <Text>Get Clear Certified: a 20-minute walk-through of the app.</Text>
      <Button
        type="button"
        className="p-button-sm"
        label={resuming ? "Resume" : "Start"}
        onClick={handleStart}
      />
      <Button
        type="button"
        icon="pi pi-times"
        className="p-button-text p-button-plain p-button-sm"
        aria-label="Dismiss"
        onClick={handleDismiss}
      />
    </Banner>
  );
}
